import { useState, useEffect } from 'react'
import { Navigate, Link, useLocation } from 'react-router-dom'
import { Search, Eye, EyeOff, CheckCircle } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Turnstile } from '@/components/Turnstile'
import { apiClient } from '@/lib/api'
import { SEOHead } from '@/components/SEOHead'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string>('')
  const [turnstileEnabled, setTurnstileEnabled] = useState(false)
  const [turnstileUnavailable, setTurnstileUnavailable] = useState(false)
  const location = useLocation()

  const { login, isAuthenticated } = useAuth()

  // Fetch Turnstile site key
  useEffect(() => {
    apiClient.getTurnstileSiteKey()
      .then((data) => {
        if (data.enabled && data.site_key) {
          setTurnstileSiteKey(data.site_key)
          setTurnstileEnabled(true)
        }
      })
      .catch(() => {
        // Silently fail - Turnstile is optional
      })
  }, [])

  // If Turnstile is enabled but widget doesn't load or errors (e.g. 300030), allow login after a timeout
  useEffect(() => {
    if (!turnstileEnabled || turnstileToken || turnstileUnavailable) return
    const t = setTimeout(() => setTurnstileUnavailable(true), 15000)
    return () => clearTimeout(t)
  }, [turnstileEnabled, turnstileToken, turnstileUnavailable])

  // Check for success message from navigation state (e.g., after registration)
  useEffect(() => {
    if (location.state && (location.state as any).message) {
      setSuccessMessage((location.state as any).message)
      // Clear state after displaying
      window.history.replaceState({}, document.title)
    }
  }, [location])

  if (isAuthenticated) {
    return <Navigate to="/app/dashboard" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    // Require Turnstile token only when enabled, widget loaded, and not marked unavailable (e.g. after error/timeout)
    if (turnstileEnabled && turnstileSiteKey && !turnstileToken && !turnstileUnavailable) {
      const widgetContainer = document.querySelector('[data-turnstile-widget]')
      if (widgetContainer) {
        setError('Please complete the security verification')
        setIsLoading(false)
        return
      }
    }

    try {
      await login(email, password, turnstileToken || undefined, rememberMe)
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Invalid email or password'
      setError(errorMessage)
      // Reset Turnstile on error
      if (turnstileEnabled) {
        setTurnstileToken(null)
        // Reset the widget if it exists - use a small delay to prevent loops
        setTimeout(() => {
          const widgetContainer = document.querySelector('[data-turnstile-widget]')
          if (widgetContainer && (window as any).turnstile) {
            try {
              const iframe = widgetContainer.querySelector('iframe')
              if (iframe) {
                // Get widget ID from iframe or try to reset by finding the widget
                const widgetId = iframe.getAttribute('data-widget-id')
                if (widgetId && (window as any).turnstile.reset) {
                  (window as any).turnstile.reset(widgetId)
                } else {
                  // Fallback: try to reset by removing and re-rendering
                  const containerDiv = widgetContainer.querySelector('div[class*="flex"]')
                  if (containerDiv && containerDiv.innerHTML) {
                    containerDiv.innerHTML = ''
                    // Widget will re-render automatically via useEffect
                  }
                }
              }
            } catch (e) {
              // Ignore reset errors - widget will reset on next interaction
              console.warn('Turnstile reset failed:', e)
            }
          }
        }, 100)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background dark flex items-center justify-center p-4 relative overflow-hidden">
      <SEOHead
        title="Sign In - Sycek OSINT Platform"
        description="Sign in to your Sycek OSINT account to access real-time threat intelligence, breach data, geospatial analysis, and advanced investigation tools."
        canonical="/login"
      />
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5"></div>
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent"></div>
      
      <Card className="relative z-10 w-full max-w-md border-border shadow-2xl">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Search className="w-5 h-5 text-primary-foreground" />
            </div>
            <h1 className="text-xl font-bold text-primary">Sycek OSINT</h1>
          </div>
          <CardTitle className="text-foreground">Welcome Back</CardTitle>
          <CardDescription className="text-muted-foreground">
            Sign in to access the OSINT investigation platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-foreground">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="analyst@sycek.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-sm font-medium text-foreground">
                  Password
                </label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="remember-me"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked === true)}
              />
              <label htmlFor="remember-me" className="text-sm text-muted-foreground cursor-pointer select-none">
                Remember me for 30 days
              </label>
            </div>

            {successMessage && (
              <div className="text-sm text-green-600 bg-green-500/10 border border-green-500/20 rounded-md p-3 flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{successMessage}</span>
              </div>
            )}

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
                {error}
              </div>
            )}

            {turnstileEnabled && (
              <>
                <Turnstile
                  siteKey={turnstileSiteKey}
                  onSuccess={(token) => { setTurnstileToken(token); setTurnstileUnavailable(false) }}
                  onError={() => { setTurnstileToken(null); setTurnstileUnavailable(true) }}
                  onExpire={() => setTurnstileToken(null)}
                  theme="auto"
                  size="normal"
                  className="my-4"
                />
                {turnstileUnavailable && (
                  <p className="text-sm text-amber-500 mt-2">
                    Verification couldn&apos;t load. You can try signing in anyway.
                  </p>
                )}
              </>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || (turnstileEnabled && !turnstileToken && !turnstileUnavailable)}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary hover:underline">
                Sign up
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-primary hover:underline">
              ← Back to Home
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}